// [2026-09-06] ADDED — the API-request feed narrowed to the video-processing API, as the second tab
// of the API Requests screen.
//
// WHY A SEPARATE VIEW AND NOT `<PlatformRequests endpoint="…" />`
// ----------------------------------------------------------------
// GET /me/requests matches `endpoint` EXACTLY (app/core/me_analytics.py:116-117), and the video API
// is a PREFIX — /proctoring/video/analyze today, and the proxy mounts its router on that prefix
// (app/core/video_processing_proxy.py:75) so a second path there is one route away. An exact-match
// filter would silently stop covering the tab the day that happens. `search` is a case-insensitive
// substring regex over `endpoint` (me_analytics.py:118-124), so searching "/proctoring/video" is
// the scoping the server can actually express, and it keeps covering the whole surface.
//
// The cost of using `search` for scope is that the user's own search box would fight it — two
// values, one query parameter. So the box here searches WITHIN the scope in the browser, over the
// page already fetched, and says so in its placeholder. That is honest about what it does and
// leaves the server query a single unambiguous term.
//
// [2026-09-11] SUPERSEDED — the two paragraphs above described the only scoping the server could
// express in September 2026. GET /me/requests now takes a `service` parameter, so this tab is
// scoped with `service=proctoring` and `search` is the user's again. The reasoning is kept because
// it is still WHY this is a separate view rather than `<PlatformRequests endpoint="…" />`: the
// exact-match `endpoint` filter still cannot express a prefix. Only the workaround changed.
//
// The prefix constant that expressed the old scope is kept below and is no longer read.
//
// WHAT A ROW IS HERE, versus the Analysis API tab on Reports
// -----------------------------------------------------------
// This is the CALL log: every submission attempt, including the ones that were REFUSED — a 402 with
// no credit, a 422 with a bad file URL, a 502 when the engine was unreachable. The Reports screen's
// Analysis API tab lists only jobs the engine ACCEPTED, because that is when an ownership row is
// written. A customer asking "why did nothing happen when I called you" is asking about this table;
// the accepted-job list cannot answer them, because a refused call never reaches it.
//
// ONLY /analyze IS METERED. The proxy's GET routes (results/{job_id}, jobs) do not call
// `backend.usage.record` — only the two `_meter` sites at video_processing_proxy.py:255 and :567 do,
// both on /analyze. Reads of a job therefore never appear here. Said in the subtitle rather than
// left for someone to notice as a gap.

import { useState } from 'react'
import { platform } from '../../api.js'
// [2026-09-24] CHANGED — `credits` added. The Request detail popup calls credits(row.credits) for a
// row that cost credits, and it was never imported: opening such a row threw a ReferenceError during
// render and blanked the page. Masked until now only because the backend had stopped storing
// credits on request rows (fixed the same day in proctoring-sdk-backend, stores.py).
// OLD: import { Modal, ms, when } from '../../ui.jsx'
import { Modal, credits, ms, when } from '../../ui.jsx'
import DataTable from './DataTable.jsx'
import { PfChip, PfNotice } from '../../components/platform/ui.jsx'

/** The prefix every video-processing route is mounted under, upstream in the proxy's router.
 *
 *  [2026-09-11] NO LONGER READ — fetchPage scopes with `service=proctoring` instead. KEPT, not
 *  deleted: it is the one written record in this app of what that prefix is, and it is what the
 *  commented-out OLD fetchPage refers to, so removing it would make that revert not compile. */
const VIDEO_PREFIX = '/proctoring/video'

// Same mapping PlatformRequests uses, and deliberately the same tones: a 402 must not look like a
// different kind of thing on this tab than it does on the one beside it.
const CLASS_TONE = { success: 'live', client_error: 'warn', server_error: 'bad' }

export default function PlatformVideoRequests({ onError }) {
  const [retention, setRetention] = useState(null)
  const [row, setRow] = useState(null)

  // [2026-09-11] CHANGED — scoped with `service`, and `search` is now forwarded to the server.
  //
  // OLD:
  //   // `search` is applied in the browser, NOT forwarded — the one `search` parameter is spent on
  //   // the endpoint scope. `total` is therefore the count of what matched locally when a term is
  //   // typed, so the "showing 1-N of N" line keeps agreeing with the rows under it.
  //   async function fetchPage({ limit, offset, search, status_class, dateFrom, dateTo, order }) {
  //     const d = await platform.requests({
  //       limit, offset, status_class, search: VIDEO_PREFIX,
  //       date_from: dateFrom, date_to: dateTo, order,
  //     })
  //     …
  //     let rows = d.requests || []
  //     if (search && search.trim()) {
  //       const needle = search.trim().toLowerCase()
  //       rows = rows.filter((r) => [r.endpoint, r.job_id, r.status_code]
  //         .some((v) => String(v ?? '').toLowerCase().includes(needle)))
  //       return { rows, total: rows.length }
  //     }
  //
  // WHY THE OLD SHAPE EXISTED, AND WHY IT NO LONGER HAS TO. The file header explains it: this tab
  // is a PREFIX and GET /me/requests only matched `endpoint` exactly, so `search` — a substring
  // regex — was the only parameter that could express the scope. Spending it on the scope left
  // nothing for the user's own search box, which is why that box filtered the already-fetched page
  // in the browser.
  //
  // GET /me/requests now takes `service` (added 2026-09-11 in the same change; see
  // app/core/me_analytics.py:_service_condition). Scope and search are two parameters again, so
  // both go to the server.
  //
  // WHAT THIS FIXES BEYOND TIDINESS: the local filter searched ONE PAGE. A job id on page 4 was
  // not found by typing it on page 1 — the table said "no results" for a row it held. It also
  // returned `rows.length` as the total, so the pager reported "1-3 of 3" while 15 jobs matched.
  // Searching now spans the whole feed and the count is the real one.
  async function fetchPage({ limit, offset, search, status_class, dateFrom, dateTo, order }) {
    try {
      const d = await platform.requests({
        limit, offset, status_class, search, service: 'proctoring',
        date_from: dateFrom, date_to: dateTo, order,
      })
      if (d.retention_days && d.retention_days !== retention) setRetention(d.retention_days)
      return { rows: d.requests || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  const columns = [
    // [2026-09-25] CHANGED — `orderSort: true`: the up/down date arrow, ordering the WHOLE result
    // set newest/oldest first through the server's `order` (see DataTable). This is the one
    // column the server sorts by (ts).
    // OLD: { key: 'ts', label: 'When', render: (r) => <span className="muted">{when(r.ts)}</span> },
    { key: 'ts', label: 'When', orderSort: true, render: (r) => <span className="muted">{when(r.ts)}</span> },
    { key: 'endpoint', label: 'Endpoint', render: (r) => <span className="mono">{r.endpoint}</span> },
    {
      key: 'status_code',
      label: 'Status',
      render: (r) => <PfChip tone={CLASS_TONE[r.status_class] || 'idle'}>{r.status_code}</PfChip>,
    },
    // The job id is the column that makes this tab worth having separately: it is the handle the
    // engine and the webhook payload both use, and it is what support asks for. An accepted call
    // has one; a refused call has none, which is itself the answer to "did my job start".
    {
      key: 'job_id',
      label: 'Job ID',
      render: (r) => (r.job_id
        ? <span className="mono">{r.job_id}</span>
        : <span className="muted">—</span>),
    },
    // [2026-09-15] CHANGED — the Latency and Credits COLUMNS are dropped from the Proctoring
    // Requests table, matching the same removal on the SDK Requests tab so the two tabs stay
    // consistent. The Job ID column above is untouched — it is the reason this tab exists
    // separately. The drill-down modal below still reports latency and credits for a single call.
    // OLD:
    // {
    //   key: 'latency_ms',
    //   label: 'Latency',
    //   align: 'right',
    //   render: (r) => (r.latency_ms == null ? '—' : ms(r.latency_ms)),
    // },
    // {
    //   key: 'credits',
    //   label: 'Credits',
    //   align: 'right',
    //   render: (r) => (r.credits ? r.credits.toFixed(3) : '—'),
    // },
  ]

  const filters = [{
    key: 'status_class',
    label: 'Status',
    options: [
      { value: 'success', label: 'Accepted (2xx)' },
      { value: 'client_error', label: 'Refused (4xx)' },
      { value: 'server_error', label: 'Engine error (5xx)' },
    ],
  }]

  return (
    <>
      {/* Stated once, above the table, because it is the difference between this log and the
          Analysis API tab on Reports — and the reason a 2xx row here is not a finished report. */}
      {/* [2026-09-25] CHANGED — this notice is OFF. Asked for by Rujal: "Remove the sentence
          'every call of the video processing' … I don't want this sentence to be there in the UI."
          `false &&` rather than a deletion, so the wording is one edit away from coming back.
          OLD: <PfNotice kind="info"> … </PfNotice> (rendered unconditionally) */}
      {false && (
      <PfNotice kind="info">
        <strong>Every call to the video-processing API, accepted or refused.</strong> A 2xx row means
        the analysis engine accepted the job and you were charged; it is not a finished report. The
        report is delivered to your configured webhook, and the engine sends no callback if an
        analysis fails after acceptance. Accepted jobs are listed under Proctoring Reports → API
        Processing.
      </PfNotice>
      )}

      {/* [2026-09-11] CHANGED — the search placeholder lost the words "this page", because they
          stopped being true in the same change: the box now searches the whole feed server-side,
          not the rows already fetched. See fetchPage above.
          OLD: searchPlaceholder="Search this page by endpoint, job id or status code…" */}
      <DataTable
        subtitle={retention
          ? `One row per video-processing call, newest first · kept for ${retention} days`
          : 'One row per video-processing call, newest first'}
        columns={columns}
        fetchPage={fetchPage}
        pageSize={25}
        searchPlaceholder="Search endpoint, job id or status code…"
        filters={filters}
        filterLabel="Video Processing"
        onRowClick={setRow}
        rowKey={(r, i) => `${r.ts}-${r.endpoint}-${i}`}
        emptyTitle="No video-processing calls yet"
        emptyHint="Calls to the analysis API appear here as soon as your backend submits a recording."
        csvName="video-processing-requests"
      />

      {row && (
        <Modal title="Request detail" subtitle={row.endpoint} onClose={() => setRow(null)}>
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            {/* [2026-09-17] CHANGED — Status and Latency dropped, and "Credits charged 0" replaced
                with what actually happened to the money. The SDK Requests modal got the same
                treatment on 2026-09-17; this tab is a SEPARATE FILE and was missed then.

                STATUS is the badge on the row you clicked to open this. LATENCY belongs on
                Analytics, where it is a trend and a P95; one call's "27ms" tells nobody anything.

                WHY "Credits charged" READ 0 ON EVERY ROW, and why the 0 was true but misleading:
                when interview billing is on, /proctoring/video/analyze MOVES NO MONEY. The credit
                was reserved when the candidate's session was minted and is settled when the
                analysis completes — video_processing_proxy.py:648-653 is explicit, "WHEN INTERVIEW
                BILLING IS ON, THIS PATH TAKES NO MONEY AT ALL", because charging here as well is
                the double-charge the model exists to remove.

                So a bare "0" beside a real, paid-for analysis asserts the analysis was free. That
                is the exact wording Rujal used on 2026-09-11 when the Credits COLUMN was retired
                from the analyses table for the same reason.

                THE NUMBER IS NOT INVENTED. When billing is per-interview the charge simply is not
                on this row — it is a settle entry on Credits & Billing, against the interview — so
                the field says that instead of printing a zero. With interview billing OFF this
                path does charge, `credits` is non-zero, and the real figure shows exactly as before.

                --- OLD (pre-2026-09-17) ---
                ['Status', `${row.status_code} (${row.status_class})`],
                ['Latency', row.latency_ms == null ? '—' : ms(row.latency_ms)],
                ['Credits charged', row.credits ? row.credits.toFixed(3) : '0'],
                --- END OLD --- */}
            {[
              ['When', when(row.ts)],
              ['Endpoint', row.endpoint],
              ['Method', row.method || '—'],
              ['Candidate / room', row.reference || '—'],
              ['Request ID', row.request_id || '—'],
              ['Credits charged', row.credits
                ? credits(row.credits)
                : 'Billed with the interview, not this call'],
              // An em dash here is not a missing value: no job id means the engine never accepted
              // the submission, which is the whole finding for a failed call.
              ['Job id', row.job_id || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10 }}>
                <span className="muted">{label}</span>
                <span className="mono">{value}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  )
}
